import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card, CardBody, Row, Col, Badge,
  Button, Input, InputGroup, Modal, ModalHeader, ModalBody,
  Alert, Spinner,
} from "reactstrap";
import {
  ChevronRight, ChevronLeft, CheckCircle,
  Search, Camera, ChevronDown, ChevronUp, AlertTriangle, Save,
  Upload, X as XIcon, Image, Printer,
} from "lucide-react";
import { PageFrame, PageBody } from "../../Components/Common/EmbeddablePage";
import {
  getDepartments, getScopes, getTypes, getFormTree, getNcTree, getTargets,
  saveFilledForm, getAnswers, saveAnswer, updateFilledForm,
  uploadPhoto, getPhotos, getPhotoUrl, getHazardNonconformities, getChildForms, getFormDetail,
} from "../../services/sahaInspectionService";
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag, currentLanguageId } from "../../i18n";
import FormFillView from "../../Components/FormFill/FormFillView";
import { ANSWER_OPTIONS } from "../../Components/FormFill/answerOptions";

// ─────────────────────────────────────────────────────────────────────────────
// SABIT DEĞERLER
// ─────────────────────────────────────────────────────────────────────────────

// inspectionLevelId bazlı konfigürasyon
// API: GET /api/v1/inspection/levels → { 1: "Denetim Raporu", 2: "Uygunsuzluk Formu", 3: "Kapalı DÖF" }
const LEVEL_CFG = {
  1: { get label() { return i18n.t("saha:sahaFormFill.kontrolFormu"); },                     color: "primary", apiLevelId: 1 },
  2: { get label() { return i18n.t("saha:sahaFormFill.uygunsuzlukFormu"); },                 color: "warning", apiLevelId: 2 },
  3: { get label() { return i18n.t("saha:sahaFormFill.duzenleyiciOnleyiciFaaliyet"); },      color: "danger",  apiLevelId: 3 },
};

const TARGET_ENTITY_LABELS = {
  get stand() { return i18n.t("saha:sahaFormFill.stand"); },
  get salon() { return i18n.t("saha:sahaFormFill.salon"); },
  get fuar_alani() { return i18n.t("saha:sahaFormFill.fuarAlani"); },
  get fuar() { return i18n.t("saha:sahaFormFill.fuar"); },
};

// ─────────────────────────────────────────────────────────────────────────────
// ADIM BİLEŞENLERİ
// ─────────────────────────────────────────────────────────────────────────────

// ── Adım 1: Birim Seçimi ──────────────────────────────────────────────────────
function StepDepartment({ onSelect }) {
  const { t } = useTranslation("saha");
  const [departments, setDepartments] = useState([]);
  const [loading,     setLoading]     = useState(true);

  useEffect(() => {
    getDepartments().then((d) => { setDepartments(d); setLoading(false); });
  }, []);

  const deptIcons = [
    "bx bxs-building-house",
    "bx bxs-buildings",
    "bx bxs-layer",
    "bx bxs-grid-alt",
    "bx bxs-briefcase",
    "bx bxs-category",
  ];

  if (loading) return (
    <div className="text-center py-5">
      <i className="bx bx-loader-alt bx-spin fs-1 text-primary" />
    </div>
  );

  return (
    <div>
      <h5 className="fw-semibold mb-1">{t("sahaFormFill.birimSecin")}</h5>
      <p className="text-muted small mb-4">{t("sahaFormFill.denetimYapacaginizBirimiBelirleyin")}</p>
      <Row className="g-3">
        {departments.length === 0 && (
          <Col><p className="text-muted py-3 text-center">{t("sahaFormFill.birimBulunamadi")}</p></Col>
        )}
        {departments.map((dept, i) => (
          <Col key={dept.inspection_department_id} xs={6} md={3}>
            <button
              className="btn btn-light border w-100 h-100 d-flex flex-column align-items-center justify-content-center p-4 rounded-3"
              style={{ minHeight: 120, transition: "all .2s" }}
              onClick={() => onSelect(dept)}
              onMouseEnter={(e) => e.currentTarget.classList.add("shadow-sm")}
              onMouseLeave={(e) => e.currentTarget.classList.remove("shadow-sm")}
            >
              <i className={`${deptIcons[i % deptIcons.length]} fs-2 text-primary mb-2`} />
              <span className="fw-semibold small text-center">
                {dept.translated_name || dept.inspection_department_name}
              </span>
              {dept.inspection_department_description && (
                <span className="text-muted text-center" style={{ fontSize: "0.7rem" }}>
                  {dept.inspection_department_description}
                </span>
              )}
            </button>
          </Col>
        ))}
      </Row>
    </div>
  );
}

// ── Adım 2: Kapsam Seçimi ─────────────────────────────────────────────────────
function StepScope({ department, onSelect, onBack }) {
  const { t } = useTranslation("saha");
  const [scopes,  setScopes]  = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getScopes({ departmentId: department?.inspection_department_id })
      .then((d) => { setScopes(d); setLoading(false); });
  }, [department?.inspection_department_id]);

  const scopeIcons = {
    stand:      "bx bxs-store",
    salon:      "bx bxs-building",
    fuar_alani: "bx bxs-map",
    fuar:       "bx bxs-calendar-star",
  };

  if (loading) return (
    <div className="text-center py-5">
      <i className="bx bx-loader-alt bx-spin fs-1 text-primary" />
    </div>
  );

  return (
    <div>
      <button className="btn btn-link p-0 text-muted small mb-3 d-flex align-items-center gap-1" onClick={onBack}>
        <ChevronLeft size={15} />{" "}{t("common:geri")}
      </button>
      <h5 className="fw-semibold mb-1">{t("sahaFormFill.kapsamSecin")}</h5>
      <p className="text-muted small mb-4">
        {t("sahaFormFill.birim")}{" "}<Badge color="primary" pill className="ms-1">
          {department?.translated_name || department?.inspection_department_name}
        </Badge>
      </p>
      <Row className="g-3">
        {scopes.map((scope) => (
          <Col key={scope.inspection_scope_id} xs={6} md={3}>
            <button
              className="btn btn-light border w-100 h-100 d-flex flex-column align-items-center justify-content-center p-4 rounded-3"
              style={{ minHeight: 120, transition: "all .2s" }}
              onClick={() => onSelect(scope)}
              onMouseEnter={(e) => e.currentTarget.classList.add("shadow-sm")}
              onMouseLeave={(e) => e.currentTarget.classList.remove("shadow-sm")}
            >
              <i className={`${scopeIcons[scope.target_entity] || "bx bxs-file"} fs-2 text-primary mb-2`} />
              <span className="fw-semibold small">{scope.inspection_scope_name}</span>
              <span className="text-muted" style={{ fontSize: "0.7rem" }}>{scope.inspection_scope_description}</span>
            </button>
          </Col>
        ))}
      </Row>
    </div>
  );
}

// ── Adım 3: Hedef Seçimi ──────────────────────────────────────────────────────
function StepTarget({ scope, department, onSelect, onBack }) {
  const { t: tx } = useTranslation("saha");
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState("");

  useEffect(() => {
    getTargets(scope.target_entity).then((d) => { setTargets(d); setLoading(false); });
  }, [scope.target_entity]);

  const filtered = targets.filter(
    (t) =>
      t.name.toLowerCase().includes(search.toLowerCase()) ||
      (t.firma && t.firma.toLowerCase().includes(search.toLowerCase())) ||
      (t.salon && t.salon.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div>
      <button className="btn btn-link p-0 text-muted small mb-3 d-flex align-items-center gap-1" onClick={onBack}>
        <ChevronLeft size={15} />{" "}{tx("common:geri")}
      </button>
      <h5 className="fw-semibold mb-1">{TARGET_ENTITY_LABELS[scope.target_entity]}{" "}{tx("sahaFormFill.secin")}</h5>
      <p className="text-muted small mb-3">
        {department && (
          <>
            <Badge color="secondary" pill className="me-1">
              {department.translated_name || department.inspection_department_name}
            </Badge>
            <i className="bx bx-chevron-right mx-1 text-muted" />
          </>
        )}
        {tx("sahaFormFill.kapsam")}{" "}<Badge color="primary" pill className="ms-1">{scope.inspection_scope_name}</Badge>
      </p>
      <InputGroup className="mb-3" style={{ maxWidth: 360 }}>
        <span className="input-group-text bg-white border-end-0">
          <Search size={15} className="text-muted" />
        </span>
        <Input
          className="border-start-0"
          placeholder={tx("sahaFormFill.ara")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </InputGroup>

      {loading ? (
        <div className="text-center py-4"><i className="bx bx-loader-alt bx-spin fs-2 text-primary" /></div>
      ) : (
        <Row className="g-2">
          {filtered.length === 0 && (
            <Col><p className="text-muted py-3 text-center">{tx("sahaFormFill.sonucBulunamadi")}</p></Col>
          )}
          {filtered.map((target) => (
            <Col key={target.id} xs={12} sm={6} md={4}>
              <button
                className="btn btn-light border w-100 text-start d-flex align-items-center gap-3 p-3 rounded-3"
                style={{ transition: "all .15s" }}
                onClick={() => onSelect(target)}
              >
                <i className="bx bxs-map-pin text-primary fs-5" />
                <div>
                  <div className="fw-semibold small">{target.name}</div>
                  {target.firma && <div className="text-muted" style={{ fontSize: "0.7rem" }}>{target.firma}</div>}
                  {target.salon && <div className="text-muted" style={{ fontSize: "0.7rem" }}>{target.salon}</div>}
                </div>
                <ChevronRight size={14} className="ms-auto text-muted" />
              </button>
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
}

// ── Adım 4: Denetim Tipi Seçimi ───────────────────────────────────────────────
function StepType({ scope, target, onSelect, onBack }) {
  const { t: tx } = useTranslation("saha");
  const [types,   setTypes]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getTypes(scope.inspection_scope_id).then((d) => { setTypes(d); setLoading(false); });
  }, [scope.inspection_scope_id]);

  const typeIcons = {
    ISG: "bx bxs-hard-hat",
    ELK: "bx bxs-bolt",
    ACD: "bx bxs-first-aid",
    TAD: "bx bxs-wrench",
    BTS: "bx bxs-star",
    SGD: "bx bxs-building",
    YGN: "bx bxs-flame",
    AGD: "bx bxs-map",
    ALT: "bx bxs-network-chart",
    FGD: "bx bxs-calendar",
  };

  return (
    <div>
      <button className="btn btn-link p-0 text-muted small mb-3 d-flex align-items-center gap-1" onClick={onBack}>
        <ChevronLeft size={15} />{" "}{tx("common:geri")}
      </button>
      <h5 className="fw-semibold mb-1">{tx("sahaFormFill.denetimTuruSecin")}</h5>
      <p className="text-muted small mb-3">
        <Badge color="primary" pill>{scope.inspection_scope_name}</Badge>
        <i className="bx bx-chevron-right mx-1 text-muted" />
        <span className="fw-semibold">{target.name}</span>
        {target.firma && <span className="text-muted ms-1 small">— {target.firma}</span>}
      </p>

      {loading ? (
        <div className="text-center py-4"><i className="bx bx-loader-alt bx-spin fs-2 text-primary" /></div>
      ) : (
        <Row className="g-3">
          {types.map((t) => (
            <Col key={t.inspection_type_id} xs={12} sm={6} md={4}>
              <button
                className="btn btn-light border w-100 text-start d-flex align-items-center gap-3 p-3 rounded-3"
                style={{ transition: "all .15s" }}
                onClick={() => onSelect(t)}
              >
                <i className={`${typeIcons[t.inspection_type_short] || "bx bxs-file-doc"} fs-4 text-primary`} />
                <div className="flex-grow-1">
                  <div className="fw-semibold small">{t.inspection_type_name}</div>
                  <div className="text-muted" style={{ fontSize: "0.7rem" }}>{t.main_question}</div>
                </div>
                <Badge color="light" className="text-muted border" style={{ fontSize: "0.65rem" }}>
                  {t.inspection_type_short}
                </Badge>
              </button>
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
}

// ── Uygunsuzluk Seçim Modali ───────────────────────────────────────────────────
// API: GET /api/v1/inspection/nonconformity-tree?questionId={questionId}
// NcModal — key prop (parent'ta `key={nc-${questionId}}`) ile her yeni soru için
// component tamamen remount olur. Bu sayede state'ler otomatik sıfırlanır,
// useEffect body'sinde synchronous setState gerekmez.
// ─── Bölüm 25-F: Nonconformity Sidebar — Hazard seçilince geçmiş kayıtları göster ─
function HazardNcSuggester({ hazardId, tree, onApply }) {
  const { t } = useTranslation("saha");
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [open,    setOpen]    = useState(true);

  useEffect(() => {
    if (!hazardId) return;
    async function load() {
      setLoading(true);
      const data = await getHazardNonconformities(hazardId);
      setItems(data);
      setLoading(false);
    }
    load();
  }, [hazardId]);

  if (!hazardId || (!loading && items.length === 0)) return null;

  const riskColor = (val) => {
    if (val > 400) return "danger";
    if (val >= 71)  return "warning";
    if (val >= 20)  return "info";
    return "success";
  };

  // Öneri tıklanınca tree'den risk+nc bul, otomatik seç
  const handleApply = (suggestion) => {
    if (!tree) return;
    const ncId = suggestion.nonconformity_id ?? suggestion.inspection_nonconformity_id;
    for (const hazard of tree.hazards) {
      for (const risk of hazard.risks) {
        const nc = risk.nonconformities.find(
          (n) => n.nonconformityId === ncId
        );
        if (nc) {
          onApply({ hazard, risk, nc });
          return;
        }
      }
    }
    // Eşleşme yoksa sadece nc adını konsola yaz
    console.warn("[HazardNcSuggester] NC eşleşmesi bulunamadı:", ncId);
  };

  return (
    <div className="mb-3 bg-light rounded border" style={{ overflow: "hidden" }}>
      <button
        type="button"
        className="w-100 d-flex align-items-center justify-content-between px-3 py-2 bg-transparent border-0 text-start"
        style={{ cursor: "pointer" }}
        onClick={() => setOpen((p) => !p)}
      >
        <span className="small fw-semibold text-primary">
          {t("sahaFormFill.gecmisKayitlardanHizliSec")}{loading ? "…" : items.length})
        </span>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {open && (
        <div className="px-2 pb-2">
          {loading && (
            <div className="text-center py-2">
              <Spinner size="sm" color="primary" />
            </div>
          )}
          {!loading && items.map((s, i) => {
            const name  = s.nonconformity_name ?? s.nonconformityName ?? "—";
            const risk  = s.risk_name ?? s.riskName ?? "";
            const score = s.risk_degeri ?? s.riskDegeri ?? 0;
            return (
              <button
                key={s.nonconformity_id ?? s.inspection_nonconformity_id ?? i}
                type="button"
                className="w-100 text-start btn btn-sm btn-light mb-1 border"
                onClick={() => handleApply(s)}
              >
                <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
                  <span className="small fw-semibold flex-grow-1">{name}</span>
                  <Badge color={riskColor(score)} pill style={{ fontSize: "0.6rem" }}>
                    {score}
                  </Badge>
                </div>
                {risk && (
                  <span className="text-muted" style={{ fontSize: "0.68rem" }}>
                    ↳ {risk}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function NcModal({ open, question, onSelect, onClose }) {
  const { t } = useTranslation("saha");
  // question prop'u mount anında belli, bu yüzden initial loading true başlar
  const [tree,      setTree]      = useState(null);
  const [loading,   setLoading]   = useState(!!question);
  const [selHazard, setSelHazard] = useState(null);

  // Boş deps [] — mount'ta bir kez çalışır; tüm setState çağrıları async .then() içinde
  useEffect(() => {
    if (!question) return;
    getNcTree(question.questionId).then((data) => {
      setTree(data);
      setLoading(false);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open || !question) return null;

  if (loading || !tree) return (
    <Modal isOpen={open} toggle={onClose} size="lg" centered>
      <ModalHeader toggle={onClose}>{t("sahaFormFill.uygunsuzlukDetayi")}</ModalHeader>
      <div className="text-center py-5"><i className="bx bx-loader-alt bx-spin fs-1 text-primary" /></div>
    </Modal>
  );

  return (
    <Modal isOpen={open} toggle={onClose} size="lg" centered scrollable>
      <ModalHeader toggle={onClose}>
        <AlertTriangle size={16} className="text-warning me-2" />
        {t("sahaFormFill.uygunsuzlukDetayi2", { questionName: tree.questionName })}
      </ModalHeader>
      <ModalBody>
        {/* Uygunsuzluk Seçimi */}
        <div className="mb-3">
          <label className="form-label fw-semibold small text-uppercase text-muted">{t("sahaFormFill.uygunsuzlukSecin")}</label>
          <Row className="g-2">
            {tree.hazards.map((h) => (
              <Col key={h.hazardId} xs={12}>
                <button
                  className={`btn w-100 text-start border rounded-3 p-2 px-3 small ${selHazard?.hazardId === h.hazardId ? "btn-warning border-warning" : "btn-light"}`}
                  onClick={() => setSelHazard(h)}
                >
                  <AlertTriangle size={14} className="me-2 text-warning" />{h.hazardName}
                </button>
              </Col>
            ))}
          </Row>
        </div>

        {/* ── Bölüm 25-F: Nonconformity Sidebar ────────────────────── */}
        {selHazard && (
          <HazardNcSuggester
            hazardId={selHazard.hazardId}
            tree={tree}
            onApply={({ hazard }) => {
              setSelHazard(hazard);
            }}
          />
        )}

        {/* Kaydet Butonu */}
        {selHazard && (
          <div className="mt-3 d-flex justify-content-end gap-2">
            <Button color="light" onClick={onClose}>{t("common:iptal")}</Button>
            <Button
              color="danger"
              onClick={() => onSelect({ hazard: selHazard })}
            >
              <AlertTriangle size={15} className="me-1" />{" "}{t("sahaFormFill.uygunsuzlukKaydet")}
            </Button>
          </div>
        )}
      </ModalBody>
    </Modal>
  );
}

// #599: the selected fair and the logged-in user, or null when either is missing.
// WAS: `|| 3` / `|| 17` fallbacks, which recorded a form on fair 3 / user 17 when nothing was stored.
function readFairAndUser() {
  const fuarId = Number(localStorage.getItem('activeFairId'));
  const userId = Number(localStorage.getItem('userId'));
  return fuarId > 0 && userId > 0 ? { fuarId, userId } : null;
}

// ── Adım 5: Form Doldurma ─────────────────────────────────────────────────────
function StepForm({ scope, target, inspType, levelId, onBack, onComplete }) {
  const { t } = useTranslation("saha");
  const [tree,         setTree]         = useState(null);
  const [formRecord,   setFormRecord]   = useState(null);
  const [answers,      setAnswers]      = useState({});
  const [initDone,     setInitDone]     = useState(false);
  const [ncModal,      setNcModal]      = useState({ open: false, question: null });
  const [saveAlert,    setSaveAlert]    = useState(false);
  const [mainAnswer,   setMainAnswer]   = useState(null);
  const [initError,    setInitError]    = useState(false);
  // { fuarId, userId } the form was opened with (#599)
  const formCtx = useRef(null);
  // childForms: { [questionId]: formDoc[] } — o sorudan açılan Level 2 formlar (3.3)
  const [childForms,   setChildForms]   = useState({});
  // photos: { [questionId]: { uploading: bool, list: photoDoc[] } }
  const [photos,       setPhotos]       = useState({});
  const photoInputRef  = useRef(null);
  const photoUploadCtx = useRef(null); // { questionId, inspectionLevelId }

  // Async init: tree + form oluştur + cevapları yükle
  useEffect(() => {
    async function init() {
      const ctx = readFairAndUser();
      if (!ctx) {
        setInitError(true);
        return;
      }
      formCtx.current = ctx;
      const { fuarId, userId } = ctx;

      const [treeData, form] = await Promise.all([
        getFormTree(inspType.inspection_type_id),
        saveFilledForm({
          inspectionLevelId:  levelId,
          inspectionTypeId:   inspType.inspection_type_id,
          inspectionScopeId:  scope.inspection_scope_id,
          scopeTargetId:      target.id,
          firmaId:            target.firmaId || null,
          fuarId,
          userId,
        }),
      ]);

      setTree(treeData);
      setFormRecord(form);
      // #599: the main question's saved answer (0 = not answered yet)
      setMainAnswer(form.mainQuestionAnswer || null);

      // latest-contents carries ids only; the hazard chip under a question reads hazardName, so take the
      // names from the detail read (#601) — otherwise the chip is lost on every reload.
      const [savedAnswers, detail] = await Promise.all([
        getAnswers(form.id),
        getFormDetail(form.id, currentLanguageId()),
      ]);
      (detail?.contents || []).forEach((c) => {
        if (c.hazardName && savedAnswers[c.questionId]) savedAnswers[c.questionId].hazardName = c.hazardName;
      });
      setAnswers(savedAnswers);

      // Sayfa yenilenince mevcut fotoğrafları geri yükle
      const existingPhotos = await getPhotos(form.id);
      if (existingPhotos?.length > 0) {
        const grouped = {};
        existingPhotos.forEach((p) => {
          const qId = p.questionId;
          if (!grouped[qId]) grouped[qId] = { uploading: false, list: [] };
          grouped[qId].list.push(p);
        });
        setPhotos(grouped);
      }

      // Mevcut child formları (Level 2) sourceQuestionId'ye göre grupla (3.3)
      const existingChildForms = await getChildForms(form.id);
      if (existingChildForms?.length > 0) {
        const cfGrouped = {};
        existingChildForms.forEach((cf) => {
          const qId = cf.sourceQuestionId;
          if (qId == null) return;
          if (!cfGrouped[qId]) cfGrouped[qId] = [];
          cfGrouped[qId].push(cf);
        });
        setChildForms(cfGrouped);
      }

      setInitDone(true);
    }
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const navigate    = useNavigate();

  // 3.1 — Ana soru kilidi: mainAnswer === 1 (Uygun/Evet/Var) değilse sorular kilitli
  const questionsLocked = mainAnswer !== 1;

  // #599: ana soru cevabı formun kendisine kaydedilir (POST /form/{id}/partial); kayıt başarısızsa eski cevaba dönülür
  function handleMainAnswer(value) {
    if (!formRecord) return;
    const previous = mainAnswer;
    setMainAnswer(value);
    updateFilledForm(formRecord.id, { mainQuestionAnswer: value }).catch((e) => {
      console.error('mainQuestionAnswer kaydedilemedi:', e);
      setMainAnswer(previous);
    });
  }

  // Soru cevaplama — categoryId de API'ye gönderiliyor (PUT /form/content)
  const handleAnswer = useCallback(async (questionId, value, extra = {}) => {
    if (!formRecord) return;
    const updated = { value, ...extra };
    setAnswers((prev) => ({ ...prev, [questionId]: updated }));
    // fire-and-forget: UI'yi bloklamaz
    saveAnswer(formRecord.id, questionId, updated, levelId).catch((e) =>
      console.error('saveAnswer hatası:', e)
    );
  }, [formRecord, levelId]);

  // Uygunsuzluk seçildi — 3.2: sadece hazard; 3.3: Level 2 form sourceQuestionId ile
  async function handleNcSelect({ hazard }) {
    if (!ncModal.question) return;

    // Adım 1: Level 1 cevabı kaydet — sadece hazard bilgisi (3.2)
    handleAnswer(ncModal.question.questionId, 2, {
      categoryId: ncModal.question.categoryId,
      hazardId:   hazard.hazardId,
      hazardName: hazard.hazardName,
    });

    // Adım 2: Level 2 (Uygunsuzluk) formu oluştur — sourceQuestionId ile (3.3)
    const { fuarId: fuarId2, userId: userId2 } = formCtx.current;
    const qId     = ncModal.question.questionId;
    const catId   = ncModal.question.categoryId;
    try {
      // checkOrSave answers a parent × source question with that question's one form (#600), so "Değiştir" gets the same form back.
      const newForm = await saveFilledForm({
        inspectionLevelId: 2,
        parentFormId:      formRecord.id,
        sourceQuestionId:  qId,
        sourceCategoryId:  catId,
        inspectionTypeId:  inspType.inspection_type_id,
        inspectionScopeId: scope.inspection_scope_id,
        scopeTargetId:     target.id,
        firmaId:           target.firmaId || null,
        fuarId:            fuarId2,
        userId:            userId2,
      });
      // Child formu state'e ekle — soru altında gösterilir (3.3); aynı form zaten varsa ikinci kez eklenmez
      if (newForm?.id) {
        setChildForms((prev) => {
          const list = prev[qId] || [];
          return list.some((f) => f.id === newForm.id) ? prev : { ...prev, [qId]: [...list, newForm] };
        });
        // Child'ın kendi içerik satırı (#601): seçilen hazard orada durur; "Değiştir" aynı satırı günceller
        await saveAnswer(newForm.id, qId, { value: 2, categoryId: catId, hazardId: hazard.hazardId }, 2, { strict: true });
      }
    } catch (err) {
      console.error('[handleNcSelect] Level 2 form oluşturulamadı:', err);
    }

    setNcModal({ open: false, question: null });
  }

  const totalQ    = tree ? tree.categories.reduce((s, c) => s + c.questions.length, 0) : 0;
  const answeredQ = Object.keys(answers).length;
  const uygunsuzQ = Object.values(answers).filter((a) => a.value === 2).length;
  const progress  = totalQ > 0 ? Math.round((answeredQ / totalQ) * 100) : 0;

  async function handleSave() {
    if (!formRecord) return;
    await updateFilledForm(formRecord.id, {
      isDone: progress === 100,
      completedAt: progress === 100 ? new Date().toISOString() : null,
      // #599: sent along so a gateway that still writes the entity default (0) over a missing field keeps the answer (backend #604)
      mainQuestionAnswer: mainAnswer || 0,
    });
    setSaveAlert(true);
    setTimeout(() => setSaveAlert(false), 3000);
  }

  // Fotoğraf yükleme akışı:
  // 1. PhotoButton'a tıklanır → context'e questionId kaydedilir → hidden input'a click
  // 2. Kullanıcı dosya seçer → handlePhotoFileChange çalışır
  // 3. uploadPhoto API çağrılır → sonuç photos state'e eklenir
  function openPhotoInput(questionId, photoLevelId = levelId) {
    photoUploadCtx.current = { questionId, inspectionLevelId: photoLevelId };
    photoInputRef.current?.click();
  }

  async function handlePhotoFileChange(e) {
    const file = e.target.files?.[0];
    if (!file || !formRecord || !photoUploadCtx.current) return;
    // input'u temizle (aynı dosya tekrar seçilebilsin)
    e.target.value = '';

    const { questionId, inspectionLevelId } = photoUploadCtx.current;
    const { fuarId } = formCtx.current;

    // Yükleniyor göstergesi
    setPhotos((prev) => ({
      ...prev,
      [questionId]: { uploading: true, list: prev[questionId]?.list || [] },
    }));

    try {
      const doc = await uploadPhoto(formRecord.id, file, {
        fuarId,
        questionId,
        inspectionLevelId,
      });
      setPhotos((prev) => ({
        ...prev,
        [questionId]: {
          uploading: false,
          list: [...(prev[questionId]?.list || []), doc],
        },
      }));
    } catch (err) {
      console.error('uploadPhoto hatası:', err);
      setPhotos((prev) => ({
        ...prev,
        [questionId]: { uploading: false, list: prev[questionId]?.list || [] },
      }));
      alert(t("sahaFormFill.fotografYuklenemediLutfenTekrarDeneyin"));
    }
  }

  // Cevap butonu: "Uygun Değil" cevabı kaydeder + uygunsuzluk penceresini açar.
  // categoryId API için gönderiliyor.
  function handleQuestionAnswer(q, cat, value) {
    if (value === 2) {
      handleAnswer(q.questionId, 2, { categoryId: cat.categoryId });
      setNcModal({ open: true, question: { ...q, categoryId: cat.categoryId } });
    } else {
      handleAnswer(q.questionId, value, { categoryId: cat.categoryId });
    }
  }

  // Sorunun altı: uygunsuzluk özeti (3.2), bağlı uygunsuzluk formları (3.3), fotoğraf
  function renderQuestionExtras(q, cat, ans) {
    const isNc = ans?.value === 2;

    // April 2026: photoRequired kaldırıldı, iki ayrı flag geldi
    // answerValue=2 (negatif) → backend sabiti, her zaman fotoğraf zorunlu
    // answerValue=1 (pozitif) → photoRequiredPositive flag'ine göre
    // answerValue=3 (nötr)    → photoRequiredNeutral flag'ine göre
    // cevap verilmemişse → en az bir flag true ise rozet göster
    const photoNeeded = (ansVal) => {
      if (ansVal === 2) return true;
      if (ansVal === 1) return !!(q.photoRequiredPositive);
      if (ansVal === 3) return !!(q.photoRequiredNeutral);
      return !!(q.photoRequiredPositive || q.photoRequiredNeutral);
    };
    const showPhotoSection = photoNeeded(ans?.value);
    const qChildForms      = childForms[q.questionId] || []; // 3.3

    return (
      <>
        {/* Uygunsuzluk Özeti — 3.2: hazardName göster */}
        {isNc && ans?.hazardName && (
          <div
            className="ms-4 rounded-3 p-2 bg-danger bg-opacity-10 border border-danger border-opacity-25 d-flex align-items-start gap-2"
            style={{ fontSize: "0.75rem" }}
          >
            <AlertTriangle size={13} className="text-danger mt-1 flex-shrink-0" />
            <div>
              <span className="fw-semibold text-danger">{ans.hazardName}</span>
              <button
                className="btn btn-link btn-sm text-danger p-0 ms-2"
                style={{ fontSize: "0.72rem" }}
                onClick={() => setNcModal({ open: true, question: { ...q, categoryId: cat.categoryId } })}
              >
                {t("sahaFormFill.degistir")}
              </button>
            </div>
          </div>
        )}

        {/* Bağlı Uygunsuzluk Formları — 3.3 */}
        {qChildForms.length > 0 && (
          <div className="ms-4 d-flex flex-wrap gap-1 mt-1">
            {qChildForms.map((cf, ci) => (
              <button
                key={cf.id || ci}
                className="btn btn-sm btn-outline-warning d-flex align-items-center gap-1"
                style={{ fontSize: '0.7rem', pointerEvents: 'auto' }}
                title={t("sahaFormFill.buSorudanOlusturulanUygunsuzlukFormunu")}
                onClick={() => window.open(`/FdsSaha/dof-print/${cf.id}`, '_blank')}
              >
                <AlertTriangle size={11} />
                {t("sahaFormFill.uyg", { v: ci + 1 })}
              </button>
            ))}
          </div>
        )}

        {/* Fotoğraf Yükle */}
        {showPhotoSection && (
          <div className="ms-4">
            <button
              className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
              style={{ width: "fit-content", fontSize: "0.75rem" }}
              disabled={photos[q.questionId]?.uploading}
              onClick={() => openPhotoInput(q.questionId, ans?.value === 2 ? 2 : levelId)}
            >
              {photos[q.questionId]?.uploading
                ? <><Spinner size="sm" className="me-1" />{t("common:yukleniyor")}</>
                : <><Camera size={13} />{" "}{t("sahaFormFill.fotografEkle")}</>
              }
            </button>
            {/* Yüklenen fotoğraflar */}
            {(photos[q.questionId]?.list || []).length > 0 && (
              <div className="d-flex flex-wrap gap-2 mt-2">
                {photos[q.questionId].list.map((p, pi) => (
                  <div
                    key={p.id || pi}
                    className="rounded border overflow-hidden d-flex align-items-center justify-content-center bg-light"
                    style={{ width: 56, height: 56, cursor: "default" }}
                    title={p.originalFileName}
                  >
                    {getPhotoUrl(p) ? (
                      <img
                        src={getPhotoUrl(p)}
                        alt={p.originalFileName}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <Image size={20} className="text-muted" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </>
    );
  }

  if (initError) {
    return (
      <div className="text-center py-5 text-muted">
        <i className="bx bx-error-circle fs-1 mb-3 d-block" />
        <p>{t("sahaFormFill.fuarVeyaOturumYok")}</p>
        <Button color="light" onClick={onBack}><ChevronLeft size={14} className="me-1" />{t("common:geri")}</Button>
      </div>
    );
  }

  // Yükleniyor
  if (!initDone) {
    return (
      <div className="text-center py-5">
        <i className="bx bx-loader-alt bx-spin fs-1 text-primary mb-3 d-block" />
        <p className="text-muted small">{t("sahaFormFill.formHazirlaniyor")}</p>
      </div>
    );
  }

  if (!tree) {
    return (
      <div className="text-center py-5 text-muted">
        <i className="bx bx-error-circle fs-1 mb-3 d-block" />
        <p>{t("sahaFormFill.buDenetimTipiIcinForm")}</p>
        <Button color="light" onClick={onBack}><ChevronLeft size={14} className="me-1" />{t("common:geri")}</Button>
      </div>
    );
  }

  return (
    <div>
      {/* Geri + Başlık */}
      <div className="d-flex align-items-center gap-2 mb-3">
        <button className="btn btn-link p-0 text-muted small d-flex align-items-center gap-1" onClick={onBack}>
          <ChevronLeft size={15} />{" "}{t("common:geri")}
        </button>
      </div>

      {/* Form Başlığı */}
      <Card className="border-0 bg-light mb-3">
        <CardBody className="py-3">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <Badge color={LEVEL_CFG[levelId]?.color || "primary"} pill>
              {LEVEL_CFG[levelId]?.label || t("sahaFormFill.denetim")}
            </Badge>
            <span className="fw-semibold">{inspType.inspection_type_name}</span>
            <i className="bx bx-chevron-right text-muted" />
            <span className="text-muted small">{scope.inspection_scope_name}</span>
            <i className="bx bx-chevron-right text-muted" />
            <span className="text-primary fw-semibold small">{target.name}</span>
            {target.firma && <span className="text-muted small">— {target.firma}</span>}
          </div>
          {/* İlerleme */}
          <div className="mt-2">
            <div className="d-flex justify-content-between small text-muted mb-1">
              <span>{answeredQ}{t("sahaFormFill.soruCevaplandi", { totalQ })}</span>
              <span>
                {uygunsuzQ > 0 && <span className="text-danger me-2">{t("sahaFormFill.uygunsuzluk", { uygunsuzQ })}</span>}
                %{progress}
              </span>
            </div>
            <div className="progress" style={{ height: 6 }}>
              <div
                className={`progress-bar bg-${progress === 100 ? "success" : "primary"}`}
                style={{ width: `${progress}%`, transition: "width .3s" }}
              />
            </div>
          </div>
        </CardBody>
      </Card>

      {saveAlert && (
        <Alert color="success" className="d-flex align-items-center gap-2 mb-3" fade={false}>
          <CheckCircle size={16} />
          <span>{t("sahaFormFill.kaydedildi")}{" "}{progress < 100 && t("sahaFormFill.formHenuzTamamlanmadi")}</span>
        </Alert>
      )}

      <FormFillView
        mainQuestion={inspType.main_question}
        mainQuestType={inspType.main_question_quest_type}
        categories={tree.categories}
        answers={answers}
        mainAnswer={mainAnswer}
        onMainAnswer={handleMainAnswer}
        onAnswer={handleQuestionAnswer}
        questionsLocked={questionsLocked}
        renderQuestionExtras={renderQuestionExtras}
      />

      {/* Kaydet/Tamamla */}
      <div className="d-flex gap-2 justify-content-end mb-5">
        <Button color="light" onClick={onBack}>
          <ChevronLeft size={15} className="me-1" />{" "}{t("common:geri")}
        </Button>
        <Button color="light" onClick={() => window.print()} title={t("sahaFormFill.formuYazdirVeyaPdfOlarak")}>
          <Printer size={15} className="me-1" />{" "}{t("sahaFormFill.yazdirPdf")}
        </Button>
        <Button
          color={progress === 100 ? "success" : "primary"}
          onClick={handleSave}
        >
          <Save size={15} className="me-1" />
          {progress === 100 ? t("sahaFormFill.formuTamamlaKaydet") : t("sahaFormFill.taslakKaydet")}
        </Button>
        {progress === 100 && (
          <Button color="success" outline onClick={onComplete}>
            <CheckCircle size={15} className="me-1" />{" "}{t("sahaFormFill.bitir")}
          </Button>
        )}
      </div>

      {/* ── YAZDIR / PDF ALANI ───────────────────────────────────────────────── */}
      {/* @media print: sadece bu alan basılır, geri kalan sayfa gizlenir        */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #fds-print-area, #fds-print-area * { visibility: visible !important; }
          #fds-print-area {
            position: absolute;
            top: 0; left: 0;
            width: 100%;
            padding: 24px;
            display: block !important;
          }
          .fds-print-no-break { page-break-inside: avoid; }
        }
      `}</style>

      <div id="fds-print-area" style={{ display: "none" }}>
        {/* Başlık */}
        <div style={{ borderBottom: "2px solid #333", paddingBottom: 10, marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>
            {LEVEL_CFG[levelId]?.label || t("sahaFormFill.denetim")} — {inspType.inspection_type_name}
          </h2>
          <div style={{ fontSize: 13, color: "#555", marginTop: 4 }}>
            <span>{t("sahaFormFill.denetimYeri")}{" "}<b>{scope.inspection_scope_name}</b></span>
            <span style={{ margin: "0 12px" }}>|</span>
            <span>{t("sahaFormFill.hedef")}{" "}<b>{target.name}</b>{target.firma ? ` — ${target.firma}` : ""}</span>
            <span style={{ margin: "0 12px" }}>|</span>
            <span>{t("sahaFormFill.tarih")}{" "}<b>{new Date().toLocaleDateString(currentLocaleTag())}</b></span>
          </div>
          <div style={{ fontSize: 13, marginTop: 4 }}>
            {t("sahaFormFill.ilerleme")}{" "}<b>{answeredQ}{t("sahaFormFill.soru", { totalQ })}</b>
            {uygunsuzQ > 0 && <span style={{ color: "#c0392b", marginLeft: 12 }}>{t("sahaFormFill.uygunsuzluk", { uygunsuzQ })}</span>}
          </div>
        </div>

        {/* Kategoriler & Sorular */}
        {tree && tree.categories.map((cat) => (
          <div key={cat.categoryId} className="fds-print-no-break" style={{ marginBottom: 20 }}>
            <div style={{ background: "#f0f0f0", padding: "6px 10px", fontWeight: "bold", fontSize: 13, borderLeft: "4px solid #333", marginBottom: 6 }}>
              {cat.categoryName}
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "#fafafa", borderBottom: "1px solid #ccc" }}>
                  <th style={{ padding: "4px 8px", textAlign: "left", width: "4%" }}>#</th>
                  <th style={{ padding: "4px 8px", textAlign: "left", width: "52%" }}>{t("sahaFormFill.soru2")}</th>
                  <th style={{ padding: "4px 8px", textAlign: "left", width: "14%" }}>{t("sahaFormFill.cevap")}</th>
                  <th style={{ padding: "4px 8px", textAlign: "left", width: "30%" }}>{t("sahaFormFill.uygunsuzluk2")}</th>
                </tr>
              </thead>
              <tbody>
                {cat.questions.map((q, idx) => {
                  const ans = answers[q.questionId];
                  const opts = ANSWER_OPTIONS[q.questType] || ANSWER_OPTIONS[1];
                  const selectedOpt = opts.find((o) => o.value === ans?.value);
                  return (
                    <tr key={q.questionId} style={{ borderBottom: "1px solid #eee" }}>
                      <td style={{ padding: "5px 8px", color: "#888" }}>{idx + 1}</td>
                      <td style={{ padding: "5px 8px" }}>{q.questionName}</td>
                      <td style={{ padding: "5px 8px", fontWeight: ans ? "bold" : "normal", color: ans?.value === 1 ? "#27ae60" : ans?.value === 2 ? "#c0392b" : "#888" }}>
                        {selectedOpt ? selectedOpt.label : "—"}
                      </td>
                      <td style={{ padding: "5px 8px", fontSize: 11, color: "#c0392b" }}>
                        {ans?.nonconformityName || ""}
                        {ans?.riskDegeri ? t("sahaFormFill.risk", { riskDegeri: ans.riskDegeri }) : ""}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}

        {/* Alt bilgi */}
        <div style={{ borderTop: "1px solid #ccc", paddingTop: 8, fontSize: 11, color: "#888", marginTop: 16 }}>
          {t("sahaFormFill.fdsFairDocumentSystemYazdirma")}{" "}{new Date().toLocaleString(currentLocaleTag())}
        </div>
      </div>

      {/* Hidden file input — fotoğraf yükleme için */}
      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={handlePhotoFileChange}
      />

      {/* Uygunsuzluk Modalı — key prop ile her açılışta sıfırlanır */}
      <NcModal
        key={`nc-${ncModal.question?.questionId ?? "closed"}`}
        open={ncModal.open}
        question={ncModal.question}
        onSelect={handleNcSelect}
        onClose={() => setNcModal({ open: false, question: null })}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ANA BİLEŞEN — SahaFormFill
// Props:
//   levelId  number  1=Denetim, 2=Uygunsuzluk, 3=DÖF
//   title    string  Breadcrumb başlığı
//   embedded boolean true → sayfa kabuğu (page-content/Container/BreadCrumb) kapanır;
//                    Denetim Formu Doldur sayfasının bir sekmesi olarak render edilir
// ─────────────────────────────────────────────────────────────────────────────
// titleKey: the routes name the page title by key, so it follows the UI language; an explicit title wins.
function SahaFormFill({ levelId = 1, title: titleProp, titleKey = "kontrolFormu", embedded = false }) {
  const { t } = useTranslation("saha");
  const title = titleProp ?? ({
    uygunsuzlukOlustur: t("sahaFormFill.baslikUygunsuzlukOlustur"),
    tadilatFormu:       t("sahaFormFill.baslikTadilatFormu"),
    ozelFormlar:        t("sahaFormFill.baslikOzelFormlar"),
  }[titleKey] || t("sahaFormFill.kontrolFormu"));
  const [step,          setStep]          = useState("dept");   // dept | scope | target | type | form
  const [selDepartment, setSelDepartment] = useState(null);
  const [selScope,      setSelScope]      = useState(null);
  const [selTarget,     setSelTarget]     = useState(null);
  const [selType,       setSelType]       = useState(null);
  const [completed,     setCompleted]     = useState(false);

  useEffect(() => {
    document.title = i18n.t("saha:sahaFormFill.fdsSaha", { title });
  }, [title]);

  const levelCfg = LEVEL_CFG[levelId] || LEVEL_CFG[1];

  // Adım göstergesi
  const STEPS     = [t("sahaFormFill.adimBirim"), t("sahaFormFill.adimKapsam"), t("sahaFormFill.adimHedef"), t("sahaFormFill.tur"), t("sahaFormFill.adimDoldur")];
  const STEP_KEYS = ["dept", "scope", "target", "type", "form"];
  const currentStepIdx = STEP_KEYS.indexOf(step);

  if (completed) {
    return (
      <PageFrame embedded={embedded}>
        <PageBody embedded={embedded} title={title} pageTitle={t("sahaFormFill.fdsSaha2")}>
          <Card className="border-0 shadow-sm">
            <CardBody className="text-center py-5">
              <CheckCircle size={56} className="text-success mb-3" />
              <h4 className="fw-bold">{t("sahaFormFill.formKaydedildi")}</h4>
              <p className="text-muted">{t("sahaFormFill.denetimFormuBasariylaTamamlandiVe")}</p>
              <div className="d-flex gap-2 justify-content-center mt-3">
                <Button color="primary" onClick={() => { setStep("dept"); setSelDepartment(null); setSelScope(null); setSelTarget(null); setSelType(null); setCompleted(false); }}>
                  {t("sahaFormFill.yeniDenetimBaslat")}
                </Button>
                <Button color="light" onClick={() => window.history.back()}>
                  {t("sahaFormFill.anaSayfayaDon")}
                </Button>
              </div>
            </CardBody>
          </Card>
        </PageBody>
      </PageFrame>
    );
  }

  return (
    <PageFrame embedded={embedded}>
      <PageBody embedded={embedded} title={title} pageTitle={t("sahaFormFill.fdsSaha2")}>

        {/* Adım Göstergesi */}
        <div className="d-flex align-items-center gap-0 mb-4">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div
                className={`d-flex align-items-center gap-1 px-2 py-1 rounded-pill small fw-semibold ${
                  i === currentStepIdx
                    ? `bg-${levelCfg.color} text-white`
                    : i < currentStepIdx
                    ? "text-success"
                    : "text-muted"
                }`}
              >
                {i < currentStepIdx
                  ? <CheckCircle size={13} />
                  : <span className={`rounded-circle d-inline-flex align-items-center justify-content-center text-white ${i === currentStepIdx ? `bg-${levelCfg.color}` : "bg-secondary bg-opacity-25 text-muted"}`}
                      style={{ width: 18, height: 18, fontSize: "0.65rem" }}>
                      {i + 1}
                    </span>
                }
                {s}
              </div>
              {i < STEPS.length - 1 && <ChevronRight size={14} className="text-muted mx-1" />}
            </React.Fragment>
          ))}
        </div>

        <Card className="border-0 shadow-sm">
          <CardBody className="p-4">
            {step === "dept" && (
              <StepDepartment
                onSelect={(dept) => { setSelDepartment(dept); setStep("scope"); }}
              />
            )}
            {step === "scope" && (
              <StepScope
                department={selDepartment}
                onSelect={(scope) => { setSelScope(scope); setStep("target"); }}
                onBack={() => setStep("dept")}
              />
            )}
            {step === "target" && selScope && (
              <StepTarget
                scope={selScope}
                department={selDepartment}
                onSelect={(target) => { setSelTarget(target); setStep("type"); }}
                onBack={() => setStep("scope")}
              />
            )}
            {step === "type" && selScope && selTarget && (
              <StepType
                scope={selScope}
                target={selTarget}
                onSelect={(type) => { setSelType(type); setStep("form"); }}
                onBack={() => setStep("target")}
              />
            )}
            {step === "form" && selScope && selTarget && selType && (
              <StepForm
                scope={selScope}
                target={selTarget}
                inspType={selType}
                levelId={levelId}
                onBack={() => setStep("type")}
                onComplete={() => setCompleted(true)}
              />
            )}
          </CardBody>
        </Card>
      </PageBody>
    </PageFrame>
  );
}

export default SahaFormFill;


